import {
	getFriendlyDateRangePresetValueForDisplay,
	MultipleDateRangePresets,
	RangedValueDelimiter,
	RangePresetValuePresets,
} from './Constants';
import { RangedPresetValue } from './Types';
import moment from 'moment';
import { FilterProviderBase, isGenericEmptyValue, QueryStringTarget } from '.';
import React from 'react';
import { Filter, FilterNotSet } from '..';
import SingleDateRangePresetFilterEdit from '../editors/SingleDateRangePresetFilterEdit';

/**
 * Abstract class used to define filter type providers that support single value as well as single range.
 * Inherit this class in order to implement your own. This adds a `getPresets` abstract method
 * that needs to be implemented, returning a dictionary with all the presets that we want to work with.
 * It also adds a `isValidRangePresetValue` method in order to help validate the values parsed from the
 * query string, to ensure they're valid.
 *
 * @export
 * @abstract
 * @class SingleRangePresetProvider
 * @implements {FilterProviderBase<RangedPresetValue>}
 */
export abstract class SingleRangePresetProvider implements FilterProviderBase<RangedPresetValue> {
	public getPresets = (): RangePresetValuePresets => ({});
	// eslint-disable-next-line no-unused-vars
	public isValidRangePresetValue = (value: RangedPresetValue): boolean => true;
	public getComponent = (): React.ComponentType => () => <>Component not set.</>;
	// eslint-disable-next-line no-unused-vars
	public getComponentProps = (filter: Filter) => ({});
	public getFriendlyValueForDisplay = (value: any): React.ReactNode => {
		if (!value) {
			return <FilterNotSet />;
		}

		return getFriendlyDateRangePresetValueForDisplay(value);
	};

	public getValueFromQueryString = (query: string): RangedPresetValue | null => {
		if (!query || !query.trim()) {
			return null;
		}

		let rangedPresetValue: RangedPresetValue | null = null;
		const presets = this.getPresets() || {};

		const rangeOrPreset = query.split(RangedValueDelimiter);

		if (rangeOrPreset.length === 2) {
			const parsedValue: RangedPresetValue = {
				fromValue: rangeOrPreset[0],
				toValue: rangeOrPreset[1],
			};

			if (!this.isValidRangePresetValue(parsedValue)) {
				throw new Error('Invalid date range preset values found.');
			}

			rangedPresetValue = parsedValue;
		} else {
			const preset = presets[query];

			if (typeof preset === 'function') {
				const presetResult: RangedPresetValue = preset();
				rangedPresetValue = presetResult;
			} else {
				const parsedValue: RangedPresetValue = {
					fromValue: rangeOrPreset[0],
					toValue: null,
				};

				if (!this.isValidRangePresetValue(parsedValue)) {
					throw new Error('Invalid date range preset values found.');
				}
				rangedPresetValue = parsedValue;
			}
		}

		return rangedPresetValue;
	};

	public getQueryStringFromValue = (value: RangedPresetValue, filter: Filter, target: QueryStringTarget): string => {
		if (!value) {
			return '';
		}

		const presets = this.getPresets();

		let fromValue = '';
		let toValue = '';

		if (value.preset) {
			// if the query string is used by the client, let's display the preset only
			// to be more user friendly.
			if (target === QueryStringTarget.Client) {
				return value.preset;
			} else {
				// if this is going to the server, attempt to get the full value
				const presetFn = presets[value.preset];

				if (presetFn) {
					const preset = presetFn(filter?.options?.ignoreTimezone);
					fromValue = !isGenericEmptyValue(preset.fromValue) ? preset.fromValue : '';
					toValue = !isGenericEmptyValue(preset.toValue) ? preset.toValue : '';
				} else {
					console.warn('Invalid preset provided:', value.preset);
					return '';
				}
			}
		} else {
			fromValue = !isGenericEmptyValue(value.fromValue) ? value.fromValue : '';
			toValue = !isGenericEmptyValue(value.toValue) ? value.toValue : '';
		}

		return `${fromValue}${RangedValueDelimiter}${toValue}`;
	};

	public isEmptyValue = (value: RangedPresetValue): boolean => {
		if (isGenericEmptyValue(value)) {
			return true;
		}

		return false;
	};
}

/**
 * Implementation of the single date range preset provider
 */
export class SingleDateRangePresetFilterProvider extends SingleRangePresetProvider {
	public getPresets = () => MultipleDateRangePresets;
	public isValidRangePresetValue = (value: RangedPresetValue) => {
		const fromValue = value.fromValue ? moment(value.fromValue) : null;
		const toValue = value.toValue ? moment(value.toValue) : null;

		const fromValueValid = fromValue === null || fromValue.isValid();
		const toValueValid = toValue === null || toValue.isValid();

		if (!fromValueValid || !toValueValid) {
			return false;
		}

		return true;
	};
	public getComponent = () => SingleDateRangePresetFilterEdit;
}
